import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { Autocomplete, TextField, Typography } from '@totalsoft/rocket-ui';
import MultilineEditor from '../../../../../components/common/inputs/MultilineEditor';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { EntityTypePartner } from '../../../constants';
import { Origination } from 'constants/contexts';
import { ServiceContext } from 'providers/ServiceProvider';

const VerificationTypeDetailComponent = ({
  verificationType,
  reports,
  consentTypes,
  allVerificationTypes,
  allKindOfVerifications,
  validityTypes,
  externalVerificationWithReportId,
  externalVerificationId,
  validityTypeNumberOfDaysId,
  reportVerificationId,
  dispatch,
  validation
}) => {
  const [activeService] = useContext(ServiceContext);
  const [t] = useTranslation();
  const isKeysFinVerification =
    verificationType.kindOfVerificationId === externalVerificationId ||
    verificationType.kindOfVerificationId === externalVerificationWithReportId;

  const onPropertyChanged = itemPropertyName => itemValue => {
    dispatch({ type: 'OnItemValuesChanged', itemPropertyName, itemValue });
  };

  const onDocumentTypeChanged = (itemValue, index) => {
    dispatch({ type: 'OnDocumentTypeIdSelectionChanged', itemValue, index });
  };

  return (
    <>
      <Prompt when={verificationType.isDirty} message={t('VerificationTypes.LeavingWithoutSaving')} />
      <Grid container spacing={5}>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            value={verificationType.verificationName}
            onChange={onPropertyChanged('verificationName')}
            label={t('VerificationTypes.VerificationName')}
            required
            error={!isValid(validation.verificationName)}
            helperText={getErrors(validation.verificationName)}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={verificationType.isActive || false}
                onChange={event => onPropertyChanged('isActive')(event.target.checked)}
                color='primary'
              />
            }
            label={t('VerificationTypes.IsActive')}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={verificationType.triggeredBySystem || false}
                onChange={event => onPropertyChanged('triggeredBySystem')(event.target.checked)}
                color='primary'
              />
            }
            label={t('VerificationTypes.TriggeredBySystem')}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <FormControlLabel
            control={
              <Checkbox
                checked={verificationType.requiresIdentifier || false}
                onChange={event => onPropertyChanged('requiresIdentifier')(event.target.checked)}
                color='primary'
              />
            }
            label={t('VerificationTypes.RequiresIdentifier')}
          />
        </Grid>
        {verificationType.entityType === EntityTypePartner && (
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              isClearable
              simpleValue
              value={verificationType.consentTypeId || ''}
              options={consentTypes}
              onChange={onPropertyChanged('consentTypeId')}
              label={t('VerificationTypes.ConsentType')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={isKeysFinVerification && verificationType.validityTypeId === validityTypeNumberOfDaysId ? 3 : 4}>
          <TextField
            fullWidth
            value={verificationType.contentType || ''}
            onChange={onPropertyChanged('contentType')}
            label={t('VerificationTypes.ContentType')}
          />
        </Grid>
        {activeService?.route === Origination.serviceRoute && isKeysFinVerification && (
          <Grid item xs={12} sm={verificationType.validityTypeId === validityTypeNumberOfDaysId ? 3 : 4}>
            <Autocomplete
              fullWidth
              value={verificationType.validityTypeId}
              options={validityTypes}
              onChange={onPropertyChanged('validityTypeId')}
              simpleValue
              label={t('VerificationTypes.ValidityType')}
              required
              error={!isValid(validation.validityTypeId)}
              helperText={getErrors(validation.validityTypeId)}
            />
          </Grid>
        )}
        {verificationType.validityTypeId === validityTypeNumberOfDaysId && (
          <Grid item xs={12} sm={isKeysFinVerification && verificationType.validityTypeId === validityTypeNumberOfDaysId ? 3 : 4}>
            <TextField
              isNumeric
              fullWidth
              value={verificationType.validityDays}
              decimalScale={0}
              label={t('VerificationTypes.ValidityDays')}
              onChange={onPropertyChanged('validityDays')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={isKeysFinVerification && verificationType.validityTypeId === validityTypeNumberOfDaysId ? 3 : 4}>
          <TextField
            fullWidth
            value={verificationType.entityType || ''}
            onChange={onPropertyChanged('entityType')}
            label={t('VerificationTypes.EntityType')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            value={verificationType.storedProcedureName || ''}
            onChange={onPropertyChanged('storedProcedureName')}
            label={t('VerificationTypes.StoredProcedureName')}
          />
        </Grid>
        <Grid item xs={12} sm={12}>
          <MultilineEditor
            formulaLabel={t('VerificationTypes.FileNameFormat')}
            formulaPropertyName='fileNameFormat'
            formulaPropertyValue={verificationType.fileNameFormat || ''}
            isReadOnly={false}
            onPropertyChanged={(name, value) => onPropertyChanged(name)(value)}
          />
        </Grid>
        <Grid container spacing={2} direction='column' item xs={6} justifyContent='flex-start' align-content='flex-start'>
          <Grid item>
            <Autocomplete
              fullWidth
              value={verificationType.kindOfVerificationId}
              options={allKindOfVerifications}
              onChange={onPropertyChanged('kindOfVerificationId')}
              simpleValue
              label={t('VerificationTypes.KindOfVerification')}
              required
              error={!isValid(validation.kindOfVerificationId)}
              helperText={getErrors(validation.kindOfVerificationId)}
            />
          </Grid>
          {(verificationType.kindOfVerificationId === externalVerificationWithReportId ||
            verificationType.kindOfVerificationId === reportVerificationId) && (
            <Grid item>
              <Autocomplete
                fullWidth
                value={verificationType.reportId}
                options={reports}
                onChange={onPropertyChanged('reportId')}
                simpleValue
                label={t('VerificationTypes.Report')}
              />
            </Grid>
          )}
        </Grid>
        <Grid container direction='column' spacing={2} item xs={6} justifyContent='flex-start' align-content='flex-start'>
          <Grid item container>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={verificationType.triggersVerifications || false}
                    onChange={event => onPropertyChanged('triggersVerifications')(event.target.checked)}
                    color='primary'
                  />
                }
                label={t('VerificationTypes.TriggersVerifications')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={verificationType.useInRiskDecision || false}
                    onChange={event => onPropertyChanged('useInRiskDecision')(event.target.checked)}
                    color='primary'
                  />
                }
                label={t('VerificationTypes.UseInRiskDecision')}
              />
            </Grid>
            {verificationType.triggeredByExternalSearch !== null && (
              <Grid item xs={12} md={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={verificationType.triggeredByExternalSearch || false}
                      onChange={event => onPropertyChanged('triggeredByExternalSearch')(event.target.checked)}
                      color='primary'
                    />
                  }
                  label={t('VerificationTypes.TriggeredByExternalSearch')}
                />
              </Grid>
            )}
          </Grid>
          {!verificationType.triggersVerifications ? null : (
            <Grid item>
              <Autocomplete
                fullWidth
                value={verificationType.parentVerificationTypeId}
                options={allVerificationTypes}
                required={verificationType.triggersVerifications}
                onChange={onPropertyChanged('parentVerificationTypeId')}
                simpleValue
                error={!isValid(validation.parentVerificationTypeId)}
                helperText={getErrors(validation.parentVerificationTypeId)}
                label={t('VerificationTypes.ParentVerification')}
              />
            </Grid>
          )}
        </Grid>
        <Grid item xs={12} sm={12} md={12}>
          <Typography variant='h6'>{t('VerificationTypes.Applicability')}</Typography>
        </Grid>
        <Grid item xs={12} sm={12}>
          <MultilineEditor
            formulaLabel={t('VerificationTypes.ApplyFormula')}
            formulaPropertyName='applyFormula'
            formulaPropertyValue={verificationType.applyFormula || ''}
            isReadOnly={false}
            onPropertyChanged={(name, value) => onPropertyChanged(name)(value)}
          />
        </Grid>
        <Grid item xs={12}>
          <Typography variant='body2'>{t('VerificationTypes.LaunchedForFollowingDocumentTypes')}</Typography>
        </Grid>
        {verificationType.documentTypes &&
          verificationType.documentTypes.map((item, idx) => {
            return (
              <Grid item key={idx} xs={12} sm={2}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={item.isSelected || false}
                      onChange={event => onDocumentTypeChanged(event.target.checked, idx)}
                      color='primary'
                    />
                  }
                  label={item.documentName}
                />
              </Grid>
            );
          })}
        {!verificationType.triggersVerifications ? null : (
          <Grid item>
            <TextField
              isNumeric
              fullWidth
              value={verificationType.groupId}
              decimalScale={0}
              label={t('VerificationTypes.Group')}
              onChange={onPropertyChanged('groupId')}
            />
          </Grid>
        )}
        <Grid item xs={12} sm={12} md={12}>
          <Typography variant='h6'>{t('VerificationTypes.SummaryResponse.Header')}</Typography>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            value={verificationType.summaryTitle || ''}
            onChange={onPropertyChanged('summaryTitle')}
            label={t('VerificationTypes.SummaryResponse.Title')}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            value={verificationType.summary || ''}
            onChange={onPropertyChanged('summary')}
            label={t('VerificationTypes.SummaryResponse.Content')}
            multiline
            rows={10}
            variant='outlined'
          />
        </Grid>
        {activeService?.route === Origination.serviceRoute && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={verificationType.fieldBaseSecurity || false}
                  onChange={event => onPropertyChanged('fieldBaseSecurity')(event.target.checked)}
                  color='primary'
                />
              }
              label={t('VerificationTypes.FieldBaseSecurity')}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

VerificationTypeDetailComponent.propTypes = {
  dispatch: PropTypes.func.isRequired,
  verificationType: PropTypes.object.isRequired,
  allVerificationTypes: PropTypes.array.isRequired,
  consentTypes: PropTypes.array.isRequired,
  reports: PropTypes.array.isRequired,
  allKindOfVerifications: PropTypes.array.isRequired,
  validityTypes: PropTypes.array.isRequired,
  externalVerificationWithReportId: PropTypes.number,
  externalVerificationId: PropTypes.number,
  validityTypeNumberOfDaysId: PropTypes.number,
  reportVerificationId: PropTypes.number,
  validation: PropTypes.object.isRequired
};

export default VerificationTypeDetailComponent;
